import { ICustomDateFormatConfig } from '../../../data-models/formatter.data-model';
import { SAMPLE_DATE } from '../../../components/FormatterSelect/formatter-meta';

/**
 * Config keys that define the *shape* of a date format. A format choice replaces all of them at
 * once; everything else in the config (timeZone, hour12, noValue, invalidValue) is edited by its
 * own field and survives the change.
 *
 * Replacing the whole set is also what keeps `Intl.DateTimeFormat` from throwing: it rejects
 * dateStyle/timeStyle combined with any explicit component field, and since every option below
 * draws from one family or the other, a mixed config is unreachable.
 */
export const DATE_FORMAT_KEYS = [
  'dateStyle',
  'timeStyle',
  'weekday',
  'era',
  'year',
  'month',
  'day',
  'dayPeriod',
  'hour',
  'minute',
  'second',
  'fractionalSecondDigits',
  'timeZoneName',
] as const satisfies readonly (keyof ICustomDateFormatConfig)[];

export interface DateFormatOption {
  id: string;
  label: string;
  group: 'Date' | 'Date and time' | 'Time';
  config: ICustomDateFormatConfig;
}

/**
 * The date shapes offered on the config page. Curated rather than generated: the full
 * Intl.DateTimeFormatOptions space runs to thousands of permutations, almost none of them useful.
 * Covers the shapes the seeded formatters already use, so those rows edit normally, plus the
 * common gaps. A config matching nothing here is pinned as its own option — see
 * `formatterFormTransformer` — so nothing is unreachable or silently rewritten.
 *
 * Samples in the comments are en-US; the label a user sees is rendered in their own locale.
 */
export const DATE_FORMAT_OPTIONS: DateFormatOption[] = [
  {
    id: 'numericDate',
    label: 'Numeric date',
    group: 'Date', // 01/01/2025
    config: { year: 'numeric', month: '2-digit', day: '2-digit' },
  },
  {
    id: 'numericDateNoPad',
    label: 'Numeric date (no leading zeros)',
    group: 'Date', // 1/1/2025
    config: { year: 'numeric', month: 'numeric', day: 'numeric' },
  },
  {
    id: 'shortDate',
    label: 'Short date',
    group: 'Date', // 1/1/25
    config: { dateStyle: 'short' },
  },
  {
    id: 'date',
    label: 'Date',
    group: 'Date', // Jan 01, 2025
    config: { year: 'numeric', month: 'short', day: '2-digit' },
  },
  {
    id: 'dateNoPad',
    label: 'Date (no leading zero)',
    group: 'Date', // Jan 1, 2025
    config: { year: 'numeric', month: 'short', day: 'numeric' },
  },
  {
    id: 'dateLongMonth',
    label: 'Date (long month)',
    group: 'Date', // January 1, 2025
    config: { year: 'numeric', month: 'long', day: 'numeric' },
  },
  {
    id: 'dateWithWeekday',
    label: 'Date with weekday',
    group: 'Date', // Wednesday, January 1, 2025
    config: { dateStyle: 'full' },
  },
  {
    id: 'dayMonth',
    label: 'Day and month',
    group: 'Date', // Jan 1
    config: { month: 'short', day: 'numeric' },
  },
  {
    id: 'monthYear',
    label: 'Month and year',
    group: 'Date', // Jan 2025
    config: { month: 'short', year: 'numeric' },
  },
  {
    id: 'monthYearLong',
    label: 'Month and year (long)',
    group: 'Date', // January 2025
    config: { month: 'long', year: 'numeric' },
  },
  {
    id: 'monthLong',
    label: 'Month',
    group: 'Date', // January
    config: { month: 'long' },
  },
  {
    id: 'year',
    label: 'Year',
    group: 'Date', // 2025
    config: { year: 'numeric' },
  },
  {
    id: 'year2Digit',
    label: 'Year (2-digit)',
    group: 'Date', // 25
    config: { year: '2-digit' },
  },

  {
    id: 'dateTime',
    label: 'Date and time',
    group: 'Date and time', // Jan 1, 2025, 12:00:00 PM
    config: { dateStyle: 'medium', timeStyle: 'medium' },
  },
  {
    id: 'dateTimeNoSeconds',
    label: 'Date and time (no seconds)',
    group: 'Date and time', // Jan 01, 2025, 12:00 PM
    config: { year: 'numeric', month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit' },
  },
  {
    id: 'numericDateTime',
    label: 'Numeric date and time',
    group: 'Date and time', // 01/01/2025, 12:00:00 PM
    config: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    },
  },

  {
    id: 'time',
    label: 'Time',
    group: 'Time', // 12:00:00 PM
    config: { timeStyle: 'medium' },
  },
  {
    id: 'timeNoSeconds',
    label: 'Time (no seconds)',
    group: 'Time', // 12:00 PM
    config: { hour: '2-digit', minute: '2-digit' },
  },
];

/** Id used for a stored config that matches no curated option; pinned as an extra choice. */
export const CURRENT_DATE_FORMAT_ID = '__current';

function formatKeysOf(config: ICustomDateFormatConfig): string {
  const entries = DATE_FORMAT_KEYS.filter((key) => config[key] !== undefined).map((key) => [
    key,
    config[key],
  ]);
  return JSON.stringify(entries);
}

const OPTION_BY_SHAPE = new Map(DATE_FORMAT_OPTIONS.map((o) => [formatKeysOf(o.config), o.id]));

/** The curated option a stored config corresponds to, or undefined when it matches none. */
export function matchDateFormatOption(config: ICustomDateFormatConfig): string | undefined {
  return OPTION_BY_SHAPE.get(formatKeysOf(config));
}

/**
 * Applies a format choice: every shape key is replaced, so switching between the dateStyle and
 * component families cannot leave a remnant of the other behind.
 */
export function applyDateFormat(config: ICustomDateFormatConfig, formatId: string | undefined) {
  const option = DATE_FORMAT_OPTIONS.find(({ id }) => id === formatId);
  if (!option) {
    return; // no match (or the pinned current format): leave the stored shape as it is
  }
  DATE_FORMAT_KEYS.forEach((key) => delete config[key]);
  Object.assign(config, option.config);
}

/**
 * Rendered sample for a format, in the viewer's locale. Always UTC so the list reads consistently
 * — the row's own Time Zone field decides what the formatter actually does.
 */
export function getDateFormatSample(config: ICustomDateFormatConfig): string {
  return new Intl.DateTimeFormat(navigator.language, { ...config, timeZone: 'UTC' }).format(SAMPLE_DATE);
}
